import { Badge, Loader, Text, Tooltip } from "@mantine/core";
import type { MantineColor } from "@mantine/core";
import { useEffect, useState } from "react";
import { useAuth } from "@/auth/useAuth";
import { loadKmsStatus, type KmsStatusDto } from "@/api/kmsStatus";
import { Timestamp } from "./Timestamp";

/**
 * Fetches a session key's KMS-material teardown status on demand (batched across all cells
 * rendered together — see `api/kmsStatus`). `undefined` while loading, `null` if the key has no
 * status / the fetch failed.
 */
function useKmsStatus(keyId: string): KmsStatusDto | null | undefined {
  const { getIdToken } = useAuth();
  const [status, setStatus] = useState<KmsStatusDto | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setStatus(undefined);
    loadKmsStatus(keyId, () => getIdToken(false)).then((s) => {
      if (alive) setStatus(s);
    });
    return () => {
      alive = false;
    };
  }, [keyId, getIdToken]);
  return status;
}

function destroyDeadlineTip(iso: string | null): string {
  return iso
    ? `Restorable until ${new Date(iso).toLocaleString()}, then permanently destroyed.`
    : "Scheduled for destruction.";
}

/** Maps the live KMS version state (+ Spanner `state`) to a badge. */
function display(s: KmsStatusDto): {
  label: string;
  color: MantineColor;
  tip: string;
} {
  switch (s.kmsVersionState) {
    case "DESTROYED":
      return {
        label: "Destroyed",
        color: "gray",
        tip: "KMS key material permanently destroyed.",
      };
    case "DESTROY_SCHEDULED":
      return {
        label: "Destroying",
        color: "gray",
        tip: destroyDeadlineTip(s.kmsDestroyTime),
      };
    case "ENABLED":
    case "DISABLED":
      // Material is still live. If the row is revoked, teardown hasn't run yet.
      return s.state === "revoked"
        ? {
            label: "Live ⚠",
            color: "orange",
            tip: "Revoked, but KMS material not yet destroyed — teardown pending.",
          }
        : {
            label: "Live",
            color: "green",
            tip: "KMS key material is live (active key).",
          };
    default:
      return {
        label: "Unknown",
        color: "gray",
        tip: `KMS version state unavailable${s.kmsVersionState ? ` (${s.kmsVersionState})` : ""}.`,
      };
  }
}

/** Compact KMS-teardown status badge for the session-key list column. */
export function KmsStatusBadge({ keyId }: { keyId: string }) {
  const status = useKmsStatus(keyId);
  if (status === undefined) return <Loader size="xs" />;
  if (status === null)
    return (
      <Text c="dimmed" size="sm">
        —
      </Text>
    );
  const d = display(status);
  return (
    <Tooltip label={d.tip} multiline w={260}>
      <Badge color={d.color} variant="light" radius="sm" tt="none">
        {d.label}
      </Badge>
    </Tooltip>
  );
}

/** KMS-teardown detail line for the session-key detail panel. */
export function KmsStatusDetail({ keyId }: { keyId: string }) {
  const status = useKmsStatus(keyId);
  if (status === undefined) return <Loader size="xs" />;
  if (status === null) return <Text c="dimmed">—</Text>;
  return (
    <Text size="sm">
      version <b>{status.kmsVersionState ?? "—"}</b>
      {status.kmsKeyDestroyedAt ? (
        <>
          {" · scheduled "}
          <Timestamp value={status.kmsKeyDestroyedAt} absolute />
        </>
      ) : null}
      {status.kmsDestroyTime ? (
        <>
          {" · restorable until "}
          <Timestamp value={status.kmsDestroyTime} absolute />
        </>
      ) : null}
    </Text>
  );
}
